import { Link } from "react-router-dom";
import AfricaNavbar from "@/components/AfricaNavbar";
import AfricaFooter from "@/components/AfricaFooter";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";

export default function AfricaAbout() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AfricaNavbar />
      <section className="max-w-[1500px] mx-auto px-6 py-16 lg:py-24">
        <div className="max-w-[820px]">
          <div className="kicker text-white/60">About</div>
          <h1 className="display text-4xl lg:text-6xl mt-3 leading-[1.02]">Africa's Independent Verification and Transaction Assurance Company.</h1>
          <p className="mt-6 text-white/75 text-[16px] leading-[1.85]">
            THOM Verify™ provides independent verification, evidence collection, due diligence and
            documented findings that help individuals, businesses, financial institutions and
            governments make informed decisions across Africa.
          </p>
          <p className="mt-4 text-white/70 text-[15px] leading-[1.85]">
            We complement — not replace — the roles of statutory authorities, regulators,
            law-enforcement agencies, legal advisers, insurers, engineers acting in a certification
            capacity and other professionals whose approvals, certifications or decisions are
            required by law. Our findings are independent, evidence-based and issued at the moment
            of verification.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-3">
          <Pillar k="Independent"  body="THOM Verify does not sell the assets it verifies. Our commercial relationship is with the party who commissions verification — never with the party being verified." />
          <Pillar k="Continental"  body="One shared trust engine. Country deployments plug in with local registrars, police databases, currencies, payment providers and inspectors." />
          <Pillar k="Institutional" body="Governance-grade evidence: signed certificates, immutable audit trails, chain-of-custody evidence vaults, and APIs designed for banks and regulators." />
        </div>

        <div className="mt-14 max-w-[820px]">
          <h2 className="display text-3xl">How we differ.</h2>
          <ul className="mt-6 space-y-4 text-[14.5px] text-white/75 leading-[1.75]">
            <li><b className="text-white">Evidence, not opinion.</b> Every finding is backed by a photograph, a document, an authorised data source or a signed inspection artefact.</li>
            <li><b className="text-white">Every certificate is verifiable.</b> Any THOM Verify certificate carries a QR-verified reference that any party can authenticate at <Link to="/verify" className="underline text-[#B08C4A]">verify a certificate</Link>.</li>
            <li><b className="text-white">Country-agnostic architecture.</b> A regulator, bank or insurer in South Africa integrates with the same APIs, same certificates and same evidence formats as one in Zimbabwe.</li>
            <li><b className="text-white">Public commitments.</b> Our service philosophy and the scope of every verification are published — no hidden clauses, no undocumented exclusions.</li>
          </ul>
        </div>

        <div className="mt-16 border border-white/10 bg-white/[0.02] p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6">
          <div className="flex-1">
            <div className="kicker text-white/60">Get in touch</div>
            <h3 className="text-2xl font-semibold mt-2">Reach the enterprise team.</h3>
            <p className="text-[13.5px] text-white/70 mt-2">Governments, regulators, banks, insurers, dealerships, mining operators, fleet operators, real-estate institutions and trade platforms — one form, one team.</p>
          </div>
          <Link to="/africa/enterprise#request"><Button size="lg" className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black">Request Enterprise Verification <ChevronRight className="ml-1.5 h-4 w-4" /></Button></Link>
        </div>
      </section>
      <AfricaFooter />
    </div>
  );
}

const Pillar = ({ k, body }) => (
  <div className="border border-white/10 bg-white/[0.02] p-6">
    <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-[#B08C4A]">{k}</div>
    <p className="text-[13.5px] text-white/75 mt-3 leading-[1.7]">{body}</p>
  </div>
);
